// @wc-ignore-file
// (Modes and role names are data, not copy.)

import type { ContainerRole } from "@shared/types/supabase.types";

/**
 * How the current user reaches a `/workspaces/$slug` route (CR-116).
 *
 * - `member`: the normal path; RLS-backed reads, the usual owner/admin gates.
 * - `incognito`: a platform superuser viewing the workspace through the
 *   admin-only read RPCs. Always read-only — the union makes `readOnly`
 *   impossible to drift from `mode`.
 * - `contributor` (CR-183): the caller only holds the contributor anchor row
 *   on this workspace (`my_workspace_access` answers `contributor`). They may
 *   open the workspace home and their projects' suggestions page, nothing else.
 */
export type WorkspaceAccess =
  | { mode: "member"; readOnly: false }
  | { mode: "incognito"; readOnly: true }
  | { mode: "contributor"; readOnly: true };

export type WorkspaceAccessMode = WorkspaceAccess["mode"];

export const MEMBER_ACCESS: WorkspaceAccess = Object.freeze({ mode: "member", readOnly: false });
export const INCOGNITO_ACCESS: WorkspaceAccess = Object.freeze({
  mode: "incognito",
  readOnly: true,
});

export const CONTRIBUTOR_ACCESS: WorkspaceAccess = Object.freeze({
  mode: "contributor",
  readOnly: true,
});

/**
 * CR-183: the access mode for the caller's `my_workspace_access` answer. Only
 * an explicit `contributor` selects the contributor mode; anything else
 * (a real role, or null when the RPC could not answer) keeps the member path,
 * whose reads RLS already guards.
 */
export function workspaceAccessFromRole(role: ContainerRole | null | undefined): WorkspaceAccess {
  return role === "contributor" ? CONTRIBUTOR_ACCESS : MEMBER_ACCESS;
}

/** Capabilities the workspace home renders from (CR-116 D-D). */
export type WorkspaceHomeAccess = {
  isOwner: boolean;
  isAdmin: boolean;
  readOnly: boolean;
};

export const INCOGNITO_HOME_ACCESS: WorkspaceHomeAccess = Object.freeze({
  isOwner: false,
  isAdmin: false,
  readOnly: true,
});

/** Member capabilities from the caller's own membership role. */
export function getMemberHomeAccess(role: string | null | undefined): WorkspaceHomeAccess {
  return { isOwner: role === "owner", isAdmin: role === "admin", readOnly: false };
}
